import React, { useState, useEffect, useCallback } from 'react';
import { getRetentionCohorts } from '../../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const LINE_COLORS = ['#218aff', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#6b7280'];

function formatCohortWeek(isoDate) {
  if (!isoDate) return '';
  const date = new Date(isoDate + 'T00:00:00');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function getCellBackground(retentionPct) {
  if (retentionPct == null || retentionPct === undefined) return 'transparent';
  const alpha = (retentionPct / 100) * 0.7;
  return `rgba(16, 185, 129, ${alpha})`;
}

function computeSummaryStats(cohorts) {
  if (!cohorts || cohorts.length === 0) {
    return { avgWeek1: null, avgWeek4: null, bestCohort: null };
  }

  let week1Sum = 0;
  let week1Count = 0;
  let week4Sum = 0;
  let week4Count = 0;
  let bestCohort = null;
  let bestWeek1 = -1;

  for (const cohort of cohorts) {
    const retentionMap = {};
    for (const r of (cohort.retention || [])) {
      retentionMap[r.week_offset] = r.retention_pct;
    }

    if (retentionMap[1] !== undefined) {
      week1Sum += retentionMap[1];
      week1Count += 1;
      if (retentionMap[1] > bestWeek1) {
        bestWeek1 = retentionMap[1];
        bestCohort = cohort.cohort_week;
      }
    }

    if (retentionMap[4] !== undefined) {
      week4Sum += retentionMap[4];
      week4Count += 1;
    }
  }

  return {
    avgWeek1: week1Count > 0 ? (week1Sum / week1Count).toFixed(1) : null,
    avgWeek4: week4Count > 0 ? (week4Sum / week4Count).toFixed(1) : null,
    bestCohort: bestCohort ? formatCohortWeek(bestCohort) : null,
    bestCohortPct: bestWeek1 >= 0 ? bestWeek1.toFixed(1) : null,
  };
}

function buildRetentionCurveData(cohorts) {
  // Take up to 6 most recent cohorts
  const recentCohorts = cohorts.slice(-6);
  if (recentCohorts.length === 0) return [];

  // Find max week offset across all selected cohorts
  let maxOffset = 0;
  for (const cohort of recentCohorts) {
    for (const r of (cohort.retention || [])) {
      if (r.week_offset > maxOffset) maxOffset = r.week_offset;
    }
  }

  // Build one data point per week offset
  const chartData = [];
  for (let w = 0; w <= maxOffset; w++) {
    const point = { week: w };
    for (const cohort of recentCohorts) {
      const key = formatCohortWeek(cohort.cohort_week);
      const entry = (cohort.retention || []).find((r) => r.week_offset === w);
      point[key] = entry ? entry.retention_pct : null;
    }
    chartData.push(point);
  }

  return chartData;
}

function getMaxWeekOffset(cohorts) {
  let max = 0;
  for (const cohort of cohorts) {
    for (const r of (cohort.retention || [])) {
      if (r.week_offset > max) max = r.week_offset;
    }
  }
  return max;
}

function ReportsRetention() {
  const [cohortData, setCohortData] = useState(null);
  const [weeks, setWeeks] = useState(12);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getRetentionCohorts(weeks);
      setCohortData(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch retention cohorts');
    } finally {
      setLoading(false);
    }
  }, [weeks]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="roles-loading">
        <div className="spinner"></div>
        <p>Loading retention data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-error">
        <p>{error}</p>
        <button className="refresh-btn" onClick={fetchData}>Retry</button>
      </div>
    );
  }

  const cohorts = cohortData?.cohorts || [];
  const maxWeekOffset = getMaxWeekOffset(cohorts);
  const summaryStats = computeSummaryStats(cohorts);
  const curveData = buildRetentionCurveData(cohorts);
  const recentCohortLabels = cohorts.slice(-6).map((c) => formatCohortWeek(c.cohort_week));

  return (
    <div className="reports-retention">
      {/* Header with controls */}
      <div className="reports-section-header">
        <h3>Cohort Retention Analysis</h3>
        <div className="period-selector">
          <label htmlFor="retention-weeks-select">Weeks:</label>
          <select
            id="retention-weeks-select"
            value={weeks}
            onChange={(e) => setWeeks(Number(e.target.value))}
            className="period-select"
          >
            <option value={8}>8 Weeks</option>
            <option value={12}>12 Weeks</option>
            <option value={16}>16 Weeks</option>
            <option value={24}>24 Weeks</option>
          </select>
          <button className="refresh-btn" onClick={fetchData} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="analytics-kpi-cards">
        <div className="analytics-kpi-card" style={{ borderLeftColor: '#218aff' }}>
          <div className="kpi-label">Avg Week 1 Retention</div>
          <div className="kpi-value" style={{ color: '#218aff' }}>
            {summaryStats.avgWeek1 != null ? `${summaryStats.avgWeek1}%` : '--'}
          </div>
          <div className="kpi-sublabel">Across all cohorts</div>
        </div>

        <div className="analytics-kpi-card" style={{ borderLeftColor: '#10b981' }}>
          <div className="kpi-label">Avg Week 4 Retention</div>
          <div className="kpi-value" style={{ color: '#10b981' }}>
            {summaryStats.avgWeek4 != null ? `${summaryStats.avgWeek4}%` : '--'}
          </div>
          <div className="kpi-sublabel">Across all cohorts</div>
        </div>

        <div className="analytics-kpi-card" style={{ borderLeftColor: '#8b5cf6' }}>
          <div className="kpi-label">Best Cohort (Week 1)</div>
          <div className="kpi-value" style={{ color: '#8b5cf6' }}>
            {summaryStats.bestCohort || '--'}
          </div>
          <div className="kpi-sublabel">
            {summaryStats.bestCohortPct != null
              ? `${summaryStats.bestCohortPct}% retention`
              : 'No data'}
          </div>
        </div>
      </div>

      {/* Retention Heatmap Table */}
      {cohorts.length === 0 ? (
        <div className="no-data" style={{ padding: '40px', textAlign: 'center' }}>
          No retention cohort data available for the selected period.
        </div>
      ) : (
        <div className="analytics-retention-heatmap">
          <table className="retention-table">
            <thead>
              <tr>
                <th className="retention-sticky-col retention-sticky-cohort">Cohort</th>
                <th className="retention-sticky-col retention-sticky-users">Users</th>
                {Array.from({ length: maxWeekOffset + 1 }, (_, i) => (
                  <th key={i} className="retention-week-header">
                    Week {i}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cohorts.map((cohort) => {
                const retentionMap = {};
                for (const r of (cohort.retention || [])) {
                  retentionMap[r.week_offset] = r;
                }

                return (
                  <tr key={cohort.cohort_week}>
                    <td className="retention-sticky-col retention-sticky-cohort">
                      {formatCohortWeek(cohort.cohort_week)}
                    </td>
                    <td className="retention-sticky-col retention-sticky-users">
                      {cohort.cohort_size}
                    </td>
                    {Array.from({ length: maxWeekOffset + 1 }, (_, i) => {
                      const entry = retentionMap[i];
                      if (!entry) {
                        return (
                          <td key={i} className="retention-cell retention-cell-empty">
                            -
                          </td>
                        );
                      }
                      return (
                        <td
                          key={i}
                          className="retention-cell"
                          style={{ backgroundColor: getCellBackground(entry.retention_pct) }}
                          title={`${entry.active_users} active users (${entry.retention_pct.toFixed(1)}%)`}
                        >
                          {entry.retention_pct.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Retention Curves Chart */}
      {curveData.length > 0 && (
        <div className="analytics-chart-container">
          <div className="analytics-chart-header">
            <h3>Retention Curves (Last {recentCohortLabels.length} Cohorts)</h3>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={curveData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis
                dataKey="week"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#333' }}
                tickLine={{ stroke: '#333' }}
                label={{ value: 'Week', position: 'insideBottomRight', offset: -5, fill: '#999' }}
              />
              <YAxis
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#333' }}
                tickLine={{ stroke: '#333' }}
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: '#fff',
                }}
                labelStyle={{ color: '#aeb9cc' }}
                itemStyle={{ color: '#fff' }}
                labelFormatter={(label) => `Week ${label}`}
                formatter={(value) => (value != null ? [`${value.toFixed(1)}%`] : ['--'])}
              />
              <Legend wrapperStyle={{ color: '#fff', paddingTop: '10px' }} />
              {recentCohortLabels.map((label, idx) => (
                <Line
                  key={label}
                  type="monotone"
                  dataKey={label}
                  name={label}
                  stroke={LINE_COLORS[idx % LINE_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3, fill: LINE_COLORS[idx % LINE_COLORS.length] }}
                  activeDot={{ r: 5, fill: LINE_COLORS[idx % LINE_COLORS.length] }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default ReportsRetention;
